import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { CarCoastGuardPermit } from '../../types';
import Input from '../Form/Input';
import Select from '../Form/Select';
import TextArea from '../Form/TextArea';
import Button from '../Form/Button';
import { useCars } from '../../hooks/useCars';
import { useBranches } from '../../hooks/useBranches';
import { useLocations } from '../../hooks/useLocations';

interface CarPermitFormProps {
  permit?: CarCoastGuardPermit;
  onSubmit: (data: Partial<CarCoastGuardPermit>) => void;
  onCancel: () => void;
}

const CarPermitForm: React.FC<CarPermitFormProps> = ({ permit, onSubmit, onCancel }) => {
  const { data: cars = [] } = useCars();
  const { data: branches = [] } = useBranches();
  const { data: locations = [] } = useLocations();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<CarCoastGuardPermit>({
    defaultValues: permit || {
      carId: '',
      carCode: '',
      permitType: 'Coast Guard',
      branchId: '',
      branchName: '',
      locationId: '',
      locationAddress: '',
      issueDate: '',
      expiryDate: '',
      status: 'pending',
      notifyDaysBeforeExpiry: 30,
      notes: ''
    }
  });

  useEffect(() => {
    if (permit) {
      reset(permit);
    }
  }, [permit, reset]);

  const selectedCarId = watch('carId');
  const selectedBranchId = watch('branchId');
  const selectedLocationId = watch('locationId');

  // Update car code when car is selected
  useEffect(() => {
    if (selectedCarId) {
      const selectedCar = cars.find((car: any) => String(car.documentId || car.id) === String(selectedCarId));
      if (selectedCar) {
        reset(prev => ({
          ...prev,
          carCode: selectedCar.code
        }));
      }
    }
  }, [selectedCarId, cars, reset]);

  // Update branch name when branch is selected
  useEffect(() => {
    if (selectedBranchId) {
      const selectedBranch = branches.find((branch: any) => String(branch.documentId || branch.id) === String(selectedBranchId));
      if (selectedBranch) {
        reset(prev => ({
          ...prev,
          branchName: selectedBranch.name
        }));
      }
    }
  }, [selectedBranchId, branches, reset]);

  // Update location address when location is selected
  useEffect(() => {
    if (selectedLocationId) {
      const selectedLocation = locations.find((location: any) => String(location.documentId || location.id) === String(selectedLocationId));
      if (selectedLocation) {
        reset(prev => ({
          ...prev,
          locationAddress: selectedLocation.address
        }));
      }
    }
  }, [selectedLocationId, locations, reset]);

  const carOptions = useMemo(() => {
    return (cars as any[]).map((car: any) => ({
      value: car.documentId || car.id,
      label: `${car.code} - ${car.ownerName} (${car.plateNumber})`
    }));
  }, [cars]);

  const branchOptions = useMemo(() => {
    return (branches as any[])
      .filter((branch: any) => branch.status === 'active')
      .map((branch: any) => ({
        value: branch.documentId || branch.id,
        label: branch.name
      }));
  }, [branches]);

  const locationOptions = useMemo(() => {
    return (locations as any[])
      .filter((location: any) => location.isActive)
      .map((location: any) => ({
        value: location.documentId || location.id,
        label: location.address
      }));
  }, [locations]);

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {permit ? 'Edit Car Permit' : 'Add New Car Permit'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Car Selection */}
            <div className="md:col-span-2">
              <Select
                label="Car *"
                {...register('carId', { required: 'Car is required' })}
                options={carOptions}
                error={errors.carId?.message}
                placeholder="Select car..."
              />
            </div>

            {/* Permit Type */}
            <div>
              <Input
                label="Permit Type *"
                {...register('permitType', { required: 'Permit type is required' })}
                error={errors.permitType?.message}
                placeholder="Coast Guard"
              />
            </div>

            {/* Branch */}
            <div>
              <Select
                label="Vessel/Headquarters *"
                {...register('branchId', { required: 'Vessel/Headquarters is required' })}
                options={branchOptions}
                error={errors.branchId?.message}
                placeholder="Select branch..."
              />
            </div>

            {/* Location */}
            <div className="md:col-span-2">
              <Select
                label="Location *"
                {...register('locationId', { required: 'Location is required' })}
                options={locationOptions}
                error={errors.locationId?.message}
                placeholder="Select location..."
              />
            </div>

            {/* Issue Date */}
            <div>
              <Input
                label="Issue Date (Optional)"
                type="date"
                {...register('issueDate')}
                error={errors.issueDate?.message}
              />
            </div>

            {/* Expiry Date */}
            <div>
              <Input
                label="Expiry Date"
                type="date"
                {...register('expiryDate', { required: 'Expiry date is required' })}
                error={errors.expiryDate?.message}
              />
            </div>


            {/* Expiry Status */}
            <div>
              <Select
                label="Expiry Status"
                {...register('status', { required: 'Expiry status is required' })}
                options={statusOptions}
                error={errors.status?.message}
              />
            </div>

            {/* Notify Days Before Expiry */}
            <div>
              <Input
                label="Notify Expiring Soon (Days)"
                type="number"
                {...register('notifyDaysBeforeExpiry', { 
                  valueAsNumber: true,
                  min: { value: 1, message: 'Must be at least 1 day' }
                })}
                error={errors.notifyDaysBeforeExpiry?.message}
                placeholder="30"
              />
              <p className="mt-1 text-xs text-gray-500">
                Number of days before expiry to notify as "Expiring Soon"
              </p>
            </div>

            {/* Attachment URL */}
            <div className="md:col-span-2">
              <Input
                label="Attachment URL (Optional)"
                {...register('attachmentUrl')}
                error={errors.attachmentUrl?.message}
                placeholder="Enter document URL or file path"
              />
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <TextArea
                label="Notes (Optional)"
                {...register('notes')}
                error={errors.notes?.message}
                placeholder="Additional notes about this permit..."
                rows={3}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {permit ? 'Update Permit' : 'Add Permit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CarPermitForm;